import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetPreBookings,
  CreatePreBooking,
  UpdatePreBooking,
  DeletePreBooking,
} from "../Api/pre_booking_api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const preBookingQueryKeys = {
  preBookings: (params?: Parameters<typeof GetPreBookings>[0]) =>
    ["preBookings", params] as const,
};

export const invalidatePreBookingQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["preBookings"] });
};

export const usePreBookingsQuery = (
  params?: Parameters<typeof GetPreBookings>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: preBookingQueryKeys.preBookings(params),
    queryFn: async () => unwrapApiResponse(await GetPreBookings(params)),
  });
};

export const useCreatePreBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreatePreBooking>[0]) =>
      CreatePreBooking(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePreBookingQueries(queryClient);
      }
    },
  });
};

export const useUpdatePreBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdatePreBooking>[0]) =>
      UpdatePreBooking(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePreBookingQueries(queryClient);
      }
    },
  });
};

export const useDeletePreBookingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeletePreBooking>[0]) =>
      DeletePreBooking(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePreBookingQueries(queryClient);
      }
    },
  });
};

export const usePreBookingService = (config?: {
  fetchPreBookingsParams?: Parameters<typeof GetPreBookings>[0];
  fetchPreBookingsEnabled?: boolean;
}) => {
  const preBookingsQuery = usePreBookingsQuery(config?.fetchPreBookingsParams, {
    enabled: config?.fetchPreBookingsEnabled !== false,
  });

  const createPreBookingMutation = useCreatePreBookingMutation();
  const updatePreBookingMutation = useUpdatePreBookingMutation();
  const deletePreBookingMutation = useDeletePreBookingMutation();

  return {
    preBookingsQuery,
    createPreBookingMutation,
    updatePreBookingMutation,
    deletePreBookingMutation,
  };
};
